import React, { useMemo, useState } from 'react';
import { StatCard } from '../components/base';

import { STAGES } from '../lib/constantes';
import { calcContractTotal, fmt } from '../lib/formato';
import { computePartnerDashboard } from '../lib/pipeline';

function DashboardView({ leads, partners, onTogglePaid }) {
  const [filterPartner, setFilterPartner] = useState('');
  const partnerData = useMemo(() => computePartnerDashboard(leads, partners), [leads, partners]);

  const exportPartnerXLS = (d) => {
    const today = new Date().toLocaleDateString('pt-BR');
    const stgLabel = s => STAGES.find(x=>x.key===s)?.label || s;
    const fmtBRL   = v => `R$ ${(Number(v)||0).toLocaleString('pt-BR',{minimumFractionDigits:2})}`;
    const fmtDate  = s => s ? new Date(s+'T00:00:00').toLocaleDateString('pt-BR') : '—';
    const funnelLeads = d.leads.filter(l => !['fechado','perdido'].includes(l.stage));
    const closedLeads = d.leads.filter(l => l.stage === 'fechado');
    const lostLeads   = d.leads.filter(l => l.stage === 'perdido');

    const th = (cols, bg='#1e3a5f') => `<tr>${cols.map(c=>`<th style="background:${bg};color:#fff;padding:7px 10px;text-align:left;font-size:11px">${c}</th>`).join('')}</tr>`;
    const sectionRow = (label, bg, emoji='') => `<tr><td colspan="5" style="background:${bg};color:#fff;font-weight:bold;padding:9px 12px;font-size:12px;letter-spacing:.3px">${emoji} ${label}</td></tr>`;
    const leadRows = (leads, rowBg, altBg, cols) => leads.map((l,i)=>`<tr style="background:${i%2===0?rowBg:altBg}">${cols(l).map(c=>`<td style="padding:6px 10px;border-bottom:1px solid rgba(0,0,0,.06);font-size:11px">${c}</td>`).join('')}</tr>`).join('');

    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
<head><meta charset="UTF-8"><style>
body{font-family:Arial,sans-serif;font-size:11px}
table{border-collapse:collapse;width:100%}
</style></head><body><table>
<tr style="background:#0f172a">
  <td colspan="5" style="padding:16px 14px;font-size:17px;font-weight:bold;color:#fff">📊 Relatório do Parceiro: ${d.partner.name}</td>
</tr>
<tr style="background:#1e293b">
  <td colspan="3" style="padding:7px 14px;color:#94a3b8;font-size:11px">✉️ ${d.partner.email||'—'}</td>
  <td colspan="2" style="padding:7px 14px;color:#94a3b8;font-size:11px;text-align:right">📅 Gerado em ${today}</td>
</tr>
<tr><td colspan="5" style="height:10px;background:#f8fafc"></td></tr>

${sectionRow(`Resumo Geral`, '#1d4ed8', '📈')}
<tr>
  <td style="background:#eff6ff;padding:8px 12px;color:#64748b;font-size:11px">Total de Leads</td>
  <td style="background:#eff6ff;padding:8px 12px;font-weight:bold;color:#1d4ed8">${d.total_leads}</td>
  <td style="background:#eff6ff;padding:8px 12px;color:#64748b">Win Rate</td>
  <td style="background:#eff6ff;padding:8px 12px;font-weight:bold;color:#059669">${d.win_rate.toFixed(1)}%</td>
  <td style="background:#eff6ff"></td>
</tr>
<tr>
  <td style="background:#f0f9ff;padding:8px 12px;color:#64748b">Pipeline Ativo</td>
  <td style="background:#f0f9ff;padding:8px 12px;font-weight:bold;color:#1d4ed8">${fmtBRL(d.total_pipeline)}</td>
  <td style="background:#f0f9ff;padding:8px 12px;color:#64748b">Receita Fechada</td>
  <td style="background:#f0f9ff;padding:8px 12px;font-weight:bold;color:#059669">${fmtBRL(d.total_closed)}</td>
  <td style="background:#f0f9ff;padding:8px 12px;font-weight:bold;color:#dc2626">Perdidos: ${lostLeads.length}</td>
</tr>
<tr>
  <td style="background:#eff6ff;padding:8px 12px;color:#64748b">No Funil</td>
  <td style="background:#eff6ff;padding:8px 12px;font-weight:bold;color:#1d4ed8">${funnelLeads.length} leads</td>
  <td style="background:#eff6ff;padding:8px 12px;color:#64748b">Fechados</td>
  <td style="background:#eff6ff;padding:8px 12px;font-weight:bold;color:#059669">${closedLeads.length} leads</td>
  <td style="background:#eff6ff"></td>
</tr>
<tr><td colspan="5" style="height:10px;background:#f8fafc"></td></tr>

${sectionRow('Distribuição por Etapa','#475569','🗂')}
${th(['Etapa','Qtd','Valor Total','',''],'#64748b')}
${d.by_stage.filter(s=>s.count>0).map((s,i)=>`<tr style="background:${i%2===0?'#f8fafc':'#f1f5f9'}">
  <td style="padding:6px 10px;border-bottom:1px solid #e2e8f0">${s.label}</td>
  <td style="padding:6px 10px;border-bottom:1px solid #e2e8f0;font-weight:bold;text-align:center">${s.count}</td>
  <td style="padding:6px 10px;border-bottom:1px solid #e2e8f0;text-align:right">${fmtBRL(s.value)}</td>
  <td></td><td></td>
</tr>`).join('')}
<tr><td colspan="5" style="height:10px;background:#f8fafc"></td></tr>

${funnelLeads.length>0 ? `
${sectionRow(`Leads no Funil (${funnelLeads.length})`, '#1d4ed8', '🔵')}
${th(['Empresa','Etapa','Valor','Próx. Abordagem','Vendedor'],'#2563eb')}
${leadRows(funnelLeads,'#eff6ff','#dbeafe', l=>[l.company_name||'—',stgLabel(l.stage),fmtBRL(l.value),l.variable_rate>0?`+${l.variable_rate}% fat.`:'—',l.seller_name||'—'])}
<tr><td colspan="5" style="height:10px;background:#f8fafc"></td></tr>` : ''}

${closedLeads.length>0 ? `
${sectionRow(`Leads Fechados (${closedLeads.length})`, '#065f46', '✅')}
${th(['Empresa','Etapa','Valor','Data Fechamento','Vendedor'],'#059669')}
${leadRows(closedLeads,'#f0fdf4','#dcfce7', l=>[l.company_name||'—',stgLabel(l.stage),fmtBRL(l.value),l.variable_rate>0?`+${l.variable_rate}% fat.`:'—',l.seller_name||'—'])}
<tr><td colspan="5" style="height:10px;background:#f8fafc"></td></tr>` : ''}

${lostLeads.length>0 ? `
${sectionRow(`Leads Perdidos (${lostLeads.length})`, '#991b1b', '❌')}
${th(['Empresa','Etapa','Valor','Motivo da Perda','Vendedor'],'#dc2626')}
${leadRows(lostLeads,'#fff5f5','#fee2e2', l=>[l.company_name||'—',stgLabel(l.stage),fmtBRL(l.value),l.loss_reason||'—',l.seller_name||'—'])}` : ''}

</table></body></html>`;
    const blob = new Blob(["\uFEFF"+html], {type:'application/vnd.ms-excel;charset=utf-8'});
    const a = Object.assign(document.createElement('a'),{href:URL.createObjectURL(blob),download:`relatorio_${d.partner.name.replace(/\s+/g,'_')}.xls`});
    document.body.appendChild(a); a.click(); a.remove();
  };

  const displayed            = filterPartner ? partnerData.filter(d=>d.partner.id===filterPartner) : partnerData;
  const allPipeline          = displayed.reduce((s,d)=>s+d.total_pipeline,0);
  const allClosed            = displayed.reduce((s,d)=>s+d.total_closed,0);
  const allLeads             = displayed.reduce((s,d)=>s+d.total_leads,0);
  const allPipelineContract  = displayed.reduce((s,d)=>s+d.pipeline_total_contract,0);
  const allClosedContract    = displayed.reduce((s,d)=>s+d.closed_total_contract,0);
  const hasContracts         = allPipelineContract !== allPipeline || allClosedContract !== allClosed;

  return <div className="space-y-4">
    <div className="flex items-center justify-between flex-wrap gap-2">
      <h2 className="text-lg font-bold text-gray-800">Dashboard Parceiros</h2>
      <div className="flex items-center gap-2">
        <select className="apple-input px-3 py-2 text-sm" value={filterPartner} onChange={e=>setFilterPartner(e.target.value)}>
          <option value="">Todos os parceiros</option>
          {partners.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {filterPartner && displayed[0] && <button onClick={()=>exportPartnerXLS(displayed[0])} className="bg-green-500 text-white text-sm px-4 py-2 rounded-full hover:bg-green-600 flex items-center gap-1 font-semibold shadow-sm">⬇️ Exportar Relatório</button>}
      </div>
    </div>
    <div className={`grid gap-3 ${hasContracts ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : 'grid-cols-3'}`}>
      <StatCard label="Total de Leads"  value={allLeads} />
      <StatCard label="Pipeline Total"  value={fmt(allPipeline)} color="text-blue-700"
        sub={hasContracts && allPipelineContract !== allPipeline ? `Contrato: ${fmt(allPipelineContract)}` : null} />
      <StatCard label="Receita Fechada" value={fmt(allClosed)} color="text-green-700"
        sub={hasContracts && allClosedContract !== allClosed ? `Contrato: ${fmt(allClosedContract)}` : null} />
      {hasContracts && <>
        <StatCard label="💰 Contrato Total (Pipeline)" value={fmt(allPipelineContract)} color="text-blue-700" />
        <StatCard label="🏆 Contrato Total (Fechados)" value={fmt(allClosedContract)} color="text-green-700" />
      </>}
    </div>
    {displayed.length===0
      ? <div className="text-center py-16 text-gray-400"><div className="text-4xl mb-2">📊</div><p>Nenhum parceiro encontrado</p></div>
      : <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {displayed.map(d=>(
            <div key={d.partner.id} className="acard rounded-2xl p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-bold text-gray-800">{d.partner.name}</div>
                  {d.partner.email && <div className="text-xs text-gray-400">{d.partner.email}</div>}
                </div>
                <button aria-label="Baixar" onClick={()=>exportPartnerCSV(d)} title="Exportar CSV" className="text-gray-400 hover:text-green-600 text-lg">⬇️</button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-black/[0.03] rounded-xl p-2"><div className="text-xl font-bold text-gray-800">{d.total_leads}</div><div className="text-xs text-gray-500">Leads</div></div>
                <div className="bg-green-50 rounded-xl p-2"><div className="text-sm font-bold text-green-600">{d.win_rate.toFixed(0)}%</div><div className="text-xs text-gray-500">Win Rate</div></div>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-gray-500">Pipeline ativo:</span><span className="font-semibold text-gray-800">{fmt(d.total_pipeline)}</span></div>
                {d.pipeline_total_contract !== d.total_pipeline && <div className="flex justify-between"><span className="text-blue-400">↳ Contrato total:</span><span className="font-semibold text-blue-600">{fmt(d.pipeline_total_contract)}</span></div>}
                <div className="flex justify-between"><span className="text-gray-500">Fechado:</span><span className="font-semibold text-green-700">{fmt(d.total_closed)}</span></div>
                {d.closed_total_contract !== d.total_closed && <div className="flex justify-between"><span className="text-green-400">↳ Contrato total:</span><span className="font-bold text-green-600">{fmt(d.closed_total_contract)}</span></div>}
                {(d.commission_paid > 0 || d.commission_due > 0) && <>
                  <div className="flex justify-between"><span className="text-gray-500">Comissão paga:</span><span className="font-semibold text-emerald-700">{fmt(d.commission_paid)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Comissão a pagar:</span><span className="font-semibold text-amber-600">{fmt(d.commission_due)}</span></div>
                </>}
              </div>
              {d.by_stage.filter(s=>s.count>0).map(s=>(
                <div key={s.stage} className="flex items-center gap-2 text-xs">
                  <div className="flex-1 truncate text-gray-500">{s.label}</div>
                  <div className="font-medium">{s.count}</div>
                  <div className="text-gray-400">{fmt(s.value)}</div>
                </div>
              ))}
              {filterPartner && d.leads.length>0 && <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-400 mb-2">Leads</p>
                <div className="space-y-1.5">
                  {d.leads.map(l=>{
                    const isClosed = l.stage === 'fechado';
                    const commission = l.partners?.find(p=>p.id===d.partner.id)?.commission_rate || 0;
                    const contractTotal = calcContractTotal(l.value, l.recurrence);
                    const hasRecurrence = contractTotal !== (Number(l.value)||0);
                    const commissionBase = isClosed ? contractTotal : null;
                    const commissionValue = commissionBase && commission ? (commissionBase * commission / 100) : null;
                    return <div key={l.id} className={`rounded-xl px-3 py-2 ${isClosed ? 'bg-green-50 border border-green-200' : 'bg-black/[0.03]'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`font-semibold truncate flex-1 ${isClosed ? 'text-green-800' : 'text-gray-700'}`}>
                          {isClosed && '✅ '}{l.company_name}
                        </span>
                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full font-medium ${isClosed ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                          {STAGES.find(s=>s.key===l.stage)?.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1 flex-wrap gap-1">
                        <span className="text-gray-500">
                          {l.value ? fmt(l.value) : '—'}
                          {hasRecurrence && <span className="ml-1 text-blue-500 font-semibold">→ {fmt(contractTotal)}</span>}
                        </span>
                        <div className="flex items-center gap-2">
                          {commission > 0 && <span className={`font-semibold ${isClosed ? 'text-green-700' : 'text-purple-600'}`}>
                            {commission}% comissão
                          </span>}
                          {commissionValue && <span className="font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                            {fmt(commissionValue)}
                          </span>}
                          {commissionValue && (() => { const pago = !!l.partners?.find(p=>p.id===d.partner.id)?.paid_at; return onTogglePaid
                            ? <button onClick={()=>onTogglePaid(l.id, d.partner.id)} title={pago ? 'Marcada como paga. Clique para desmarcar' : 'Clique para marcar como paga'}
                                className={`text-xs px-2 py-0.5 rounded-full font-semibold transition-colors ${pago ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-amber-50 text-amber-600 hover:bg-amber-100'}`}>{pago ? '✓ Pago' : '○ A pagar'}</button>
                            : (pago ? <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-emerald-100 text-emerald-700">✓ Pago</span> : null); })()}
                        </div>
                      </div>
                    </div>;
                  })}
                </div>
              </div>}
            </div>
          ))}
        </div>}
  </div>;
}

// ─── OriginFunnelSection ──────────────────────────────────

export { DashboardView };
